import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useModalToastSlot } from '@/components/common/modalToastSlot'

export interface ClinicalSummaryToastAction {
  label: string
  onAction: () => void
}

export interface ClinicalSummaryDecisionToastProps {
  message: string | null
  /** Rendered after the message; choosing it also dismisses the toast. */
  action?: ClinicalSummaryToastAction | null
  onDismiss: () => void
  /** Auto-hide delay in ms, restarted when the pointer or focus leaves the toast. */
  durationMs?: number
  /** Move focus onto the toast when it appears, so its action is the next Tab stop. */
  takeFocus?: boolean
  /** Where focus goes when the toast closes while focus is inside it. */
  onFocusReturn?: () => void
}

/** Figma Specs: a toast with Undo auto-dismisses after 5s — the time budgeted to read it and reach Undo. */
const AUTO_DISMISS_MS = 5000

const SURFACE_CLASS =
  'pointer-events-auto inline-flex items-center gap-3.5 whitespace-nowrap rounded-full bg-background-dark px-4 py-2 text-sm font-normal leading-5 text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600'

/** Figma R09 / S04 — brief confirmation after a phenotype change, centred above the modal footer. */
export function ClinicalSummaryDecisionToast({
  message,
  action,
  onDismiss,
  durationMs = AUTO_DISMISS_MS,
  takeFocus = false,
  onFocusReturn,
}: ClinicalSummaryDecisionToastProps) {
  const slot = useModalToastSlot()
  const surfaceRef = useRef<HTMLDivElement>(null)
  const focusInside = useRef(false)
  // Keyed by message: a toast that closes under the pointer fires no mouseleave, and must not hold the next one.
  const [hoveredMessage, setHoveredMessage] = useState<string | null>(null)
  const [focusedMessage, setFocusedMessage] = useState<string | null>(null)
  const held = message !== null && (hoveredMessage === message || focusedMessage === message)
  // Held in a ref so a parent that passes a fresh callback each render cannot restart the auto-hide timer.
  const onDismissRef = useRef(onDismiss)
  onDismissRef.current = onDismiss

  // Nor does it blur, so a later toast with the same copy would still count as held.
  useEffect(() => {
    if (message) return
    setHoveredMessage(null)
    setFocusedMessage(null)
  }, [message])

  useEffect(() => {
    if (!message || held) return
    const timer = window.setTimeout(() => onDismissRef.current(), durationMs)
    return () => window.clearTimeout(timer)
  }, [message, held, durationMs])

  useLayoutEffect(() => {
    if (message && takeFocus) surfaceRef.current?.focus({ preventScroll: true })
  }, [message, takeFocus])

  // A removed node fires no blur, so focus that was inside the toast is handed back here.
  useEffect(() => {
    if (message || !focusInside.current) return
    focusInside.current = false
    if (document.activeElement === document.body || document.activeElement === null) onFocusReturn?.()
  }, [message, onFocusReturn])

  if (!message) return null

  const toast = (
    <div
      ref={surfaceRef}
      role="status"
      aria-live="polite"
      tabIndex={takeFocus ? -1 : undefined}
      className={slot ? SURFACE_CLASS : `${SURFACE_CLASS} fixed bottom-6 left-1/2 z-50 -translate-x-1/2`}
      data-testid="clinical-summary-decision-toast"
      onMouseEnter={() => setHoveredMessage(message)}
      onMouseLeave={() => setHoveredMessage(null)}
      onFocus={() => {
        focusInside.current = true
        setFocusedMessage(message)
      }}
      onBlur={(event) => {
        if (event.currentTarget.contains(event.relatedTarget as Node | null)) return
        focusInside.current = false
        setFocusedMessage(null)
      }}
    >
      <span>{message}</span>
      {action ? (
        <button
          type="button"
          onClick={() => {
            action.onAction()
            onDismiss()
          }}
          className="rounded-sm text-sm font-medium leading-5 text-text-fg-brand-subtle dark:text-brand-400 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
        >
          {action.label}
        </button>
      ) : null}
    </div>
  )

  return slot ? createPortal(toast, slot) : toast
}
